import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FileText,
  Download,
  Users,
  Mail,
  RefreshCw,
  ArrowLeft,
  Eye,
  X,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ExternalLink,
  Save,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { getMinistryLabel, normalizeMinistryKeys } from "../constants/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

async function getCsrfToken() {
  const cookieToken = getCookie("ysq_csrf");
  if (cookieToken) return cookieToken;

  const storedToken = localStorage.getItem("ysq_csrf");
  if (storedToken) return storedToken;

  const response = await axios.get(`${API}/auth/csrf`, {
    withCredentials: true,
  });

  const csrfToken = response.data?.csrf_token;
  if (csrfToken) {
    localStorage.setItem("ysq_csrf", csrfToken);
  }

  return csrfToken;
}

function getAuthHeader() {
  return {
    withCredentials: true,
  };
}

async function getJsonAuthHeader() {
  const csrf = await getCsrfToken();

  return {
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...(csrf ? { "X-CSRF-Token": csrf } : {}),
    },
  };
}

function getApplicationMinistries(app) {
  const rawMinistries =
    app.preferred_programs ||
    app.preferred_ministries ||
    app.ministries ||
    app.programs ||
    [];

  if (!Array.isArray(rawMinistries)) return [];

  return normalizeMinistryKeys(rawMinistries);
}

function getStatusClass(status) {
  switch (status) {
    case "approved":
      return "bg-green-100 text-green-700";
    case "denied":
      return "bg-scarlett/10 text-scarlett";
    case "needs_more_info":
      return "bg-blue-100 text-blue-700";
    case "probationary":
      return "bg-orange-100 text-orange-700";
    case "pending":
    default:
      return "bg-kingdom-gold/10 text-kingdom-gold";
  }
}

function formatStatus(status) {
  if (!status) return "pending";
  return status.replaceAll("_", " ");
}

function splitLinks(value) {
  if (!value) return [];

  return value
    .split(/[\n, ]+/)
    .map((link) => link.trim())
    .filter(Boolean);
}

function normalizeUrl(url) {
  if (!url) return "#";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `https://${url}`;
}

function formatDate(value) {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return "-";
  }
}

function formatDateTime(value) {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return "-";
  }
}

export default function AdminSubmissions() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [leads, setLeads] = useState([]);
  const [nationals, setNationals] = useState([]);
  const [emailContacts, setEmailContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("leads");

  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applicationModalOpen, setApplicationModalOpen] = useState(false);

  const [reviewForm, setReviewForm] = useState({
    status: "pending",
    review_notes: "",
    decision_reason: "",
    probation_status: "",
    probation_notes: "",
    probation_until: "",
    send_email: true,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const [leadsRes, nationalsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/leads`, getAuthHeader()),
        axios.get(`${API}/nationals`, getAuthHeader()),
        axios.get(`${API}/admin/email-contacts`, getAuthHeader()).catch(() => ({
          data: [],
        })),
      ]);

      setLeads(leadsRes.data || []);
      setNationals(nationalsRes.data || []);
      setEmailContacts(contactsRes.data || []);
    } catch (error) {
      const detail =
        error.response?.data?.detail || "Failed to fetch submissions";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/nationals/me`, getAuthHeader());
      const userData = response.data;
      const roles = userData.roles || [];

      if (!roles.includes("admin")) {
        toast.error("Admin access required");
        navigate("/portal");
        return;
      }

      setUser(userData);
      fetchData();
    } catch {
      navigate("/nationals/login");
    }
  }, [navigate, fetchData]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleExportCSV = async (type) => {
    try {
      const response = await axios.get(`${API}/admin/${type}/csv`, {
        ...getAuthHeader(),
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", `${type}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(`${type} CSV exported`);
    } catch {
      toast.error("Failed to export CSV");
    }
  };

  const openApplication = async (app) => {
    try {
      setActionLoading(true);

      const response = await axios.get(
        `${API}/admin/national-applications/${app.id}`,
        getAuthHeader()
      );

      const fullApp = response.data;

      setSelectedApplication(fullApp);
      setReviewForm({
        status: fullApp.status || "pending",
        review_notes: fullApp.review_notes || "",
        decision_reason: fullApp.decision_reason || "",
        probation_status: fullApp.probation_status || "",
        probation_notes: fullApp.probation_notes || "",
        probation_until: fullApp.probation_until || "",
        send_email: true,
      });

      setApplicationModalOpen(true);
    } catch (error) {
      const detail =
        error.response?.data?.detail || "Failed to open application";
      toast.error(detail);
    } finally {
      setActionLoading(false);
    }
  };

  const closeApplication = () => {
    setApplicationModalOpen(false);
    setSelectedApplication(null);
  };

  const updateApplicationInState = (updatedApp) => {
    if (!updatedApp?.id) return;

    setSelectedApplication(updatedApp);
    setNationals((current) =>
      current.map((item) => (item.id === updatedApp.id ? updatedApp : item))
    );
  };

  const handleReviewSave = async () => {
    if (!selectedApplication?.id) return;

    try {
      setActionLoading(true);

      const payload = {
        status: reviewForm.status,
        review_notes: reviewForm.review_notes || null,
        decision_reason: reviewForm.decision_reason || null,
        probation_status: reviewForm.probation_status || null,
        probation_notes: reviewForm.probation_notes || null,
        probation_until: reviewForm.probation_until || null,
        send_email: false,
      };

      const config = await getJsonAuthHeader();

      const response = await axios.patch(
        `${API}/admin/national-applications/${selectedApplication.id}/review`,
        payload,
        config
      );

      updateApplicationInState(response.data);
      toast.success("Application review saved");
    } catch (error) {
      const detail =
        error.response?.data?.detail || "Failed to save review";
      toast.error(detail);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedApplication?.id) return;

    const confirmed = window.confirm(
      "Approve this YaSHaRUN application? This may create or update a portal account."
    );

    if (!confirmed) return;

    try {
      setActionLoading(true);

      const payload = {
        review_notes: reviewForm.review_notes || null,
        decision_reason: reviewForm.decision_reason || null,
        probation_status: reviewForm.probation_status || null,
        probation_notes: reviewForm.probation_notes || null,
        probation_until: reviewForm.probation_until || null,
        send_email: Boolean(reviewForm.send_email),
      };

      const config = await getJsonAuthHeader();

      const response = await axios.post(
        `${API}/admin/national-applications/${selectedApplication.id}/approve`,
        payload,
        config
      );

      updateApplicationInState(response.data.application);
      toast.success("Application approved");

      if (response.data.temporary_password) {
        toast.message("Temporary password generated", {
          description: response.data.temporary_password,
        });
      }
    } catch (error) {
      const detail =
        error.response?.data?.detail || "Failed to approve application";
      toast.error(detail);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeny = async () => {
    if (!selectedApplication?.id) return;

    const confirmed = window.confirm("Deny this YaSHaRUN application?");

    if (!confirmed) return;

    try {
      setActionLoading(true);

      const payload = {
        review_notes: reviewForm.review_notes || null,
        decision_reason: reviewForm.decision_reason || null,
        probation_status: null,
        probation_notes: null,
        probation_until: null,
        send_email: Boolean(reviewForm.send_email),
      };

      const config = await getJsonAuthHeader();

      const response = await axios.post(
        `${API}/admin/national-applications/${selectedApplication.id}/deny`,
        payload,
        config
      );

      updateApplicationInState(response.data.application);
      toast.success("Application denied");
    } catch (error) {
      const detail =
        error.response?.data?.detail || "Failed to deny application";
      toast.error(detail);
    } finally {
      setActionLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-600">Loading...</p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-slate-100"
      data-testid="admin-submissions-page"
    >
      <section className="bg-royal-blue text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/portal")}
                className="text-white/70 hover:text-white"
                type="button"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              <div>
                <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">
                  Admin Panel
                </span>

                <h1 className="font-heading text-2xl font-bold mt-1">
                  Submissions Viewer
                </h1>
              </div>
            </div>

            <Button
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={fetchData}
              disabled={loading}
              type="button"
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between mb-6 gap-4">
              <TabsList>
                <TabsTrigger value="leads" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Leads ({leads.length})
                </TabsTrigger>

                <TabsTrigger
                  value="nationals"
                  className="flex items-center gap-2"
                >
                  <Users className="w-4 h-4" />
                  Applications ({nationals.length})
                </TabsTrigger>

                <TabsTrigger
                  value="emailContacts"
                  className="flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Email List ({emailContacts.length})
                </TabsTrigger>
              </TabsList>

              {activeTab !== "emailContacts" && (
                <Button
                  onClick={() => handleExportCSV(activeTab)}
                  className="btn-gold"
                  disabled={loading}
                  type="button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              )}
            </div>

            <TabsContent value="leads">
              <Card>
                <CardContent className="p-0">
                  {leads.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      No leads found
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="table-official w-full">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Country</th>
                            <th>Location</th>
                            <th>Interest</th>
                            <th>Social Links</th>
                            <th>Message</th>
                            <th>Date</th>
                          </tr>
                        </thead>

                        <tbody>
                          {leads.map((lead) => (
                            <tr key={lead.id}>
                              <td className="font-medium">{lead.full_name}</td>
                              <td>{lead.email}</td>
                              <td>{lead.phone}</td>
                              <td>{lead.country}</td>
                              <td className="text-sm text-slate-500">
                                {[lead.city, lead.state_province_region]
                                  .filter(Boolean)
                                  .join(", ") || "-"}
                              </td>
                              <td>
                                <span className="px-2 py-1 bg-kingdom-gold/10 text-kingdom-gold rounded text-xs">
                                  {lead.interest || "-"}
                                </span>
                              </td>
                              <td className="text-sm max-w-xs">
                                {splitLinks(lead.social_profile_links).length > 0 ? (
                                  <div className="space-y-1">
                                    {splitLinks(lead.social_profile_links).map(
                                      (link) => (
                                        <a
                                          key={link}
                                          href={normalizeUrl(link)}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-royal-blue hover:underline flex items-center gap-1"
                                        >
                                          <ExternalLink className="w-3 h-3" />
                                          {link}
                                        </a>
                                      )
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              <td className="text-sm text-slate-500 max-w-xs">
                                <span title={lead.message || ""}>
                                  {lead.message || "-"}
                                </span>
                              </td>
                              <td className="text-sm text-slate-500">
                                {formatDate(lead.created_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="nationals">
              <Card>
                <CardContent className="p-0">
                  {nationals.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      No applications found
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="table-official w-full">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Country</th>
                            <th>Status</th>
                            <th>Preferred Ministries</th>
                            <th>Social Links</th>
                            <th>Date</th>
                            <th>Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {nationals.map((app) => {
                            const ministries = getApplicationMinistries(app);
                            const links = splitLinks(app.social_links);

                            return (
                              <tr key={app.id}>
                                <td className="font-medium">{app.full_name}</td>
                                <td>{app.email}</td>
                                <td>{app.country}</td>
                                <td>
                                  <span
                                    className={`px-2 py-1 rounded text-xs capitalize ${getStatusClass(
                                      app.status
                                    )}`}
                                  >
                                    {formatStatus(app.status)}
                                  </span>
                                </td>

                                <td className="min-w-[220px]">
                                  {ministries.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {ministries.slice(0, 4).map((m) => (
                                        <span
                                          key={m}
                                          className="px-2 py-1 bg-royal-purple/10 text-royal-purple rounded text-xs"
                                          title={getMinistryLabel(m)}
                                        >
                                          {getMinistryLabel(m)}
                                        </span>
                                      ))}

                                      {ministries.length > 4 && (
                                        <span className="px-2 py-1 bg-slate-100 text-slate-500 rounded text-xs">
                                          +{ministries.length - 4} more
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-sm text-slate-400">
                                      None selected
                                    </span>
                                  )}
                                </td>

                                <td className="text-sm max-w-xs">
                                  {links.length > 0 ? (
                                    <div className="space-y-1">
                                      {links.slice(0, 2).map((link) => (
                                        <a
                                          key={link}
                                          href={normalizeUrl(link)}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-royal-blue hover:underline flex items-center gap-1"
                                        >
                                          <ExternalLink className="w-3 h-3" />
                                          {link}
                                        </a>
                                      ))}
                                      {links.length > 2 && (
                                        <span className="text-xs text-slate-500">
                                          +{links.length - 2} more
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>

                                <td className="text-sm text-slate-500">
                                  {formatDate(app.created_at)}
                                </td>

                                <td>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    type="button"
                                    onClick={() => openApplication(app)}
                                    disabled={actionLoading}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    View
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="emailContacts">
              <Card>
                <CardContent className="p-0">
                  {emailContacts.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      No email contacts found
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="table-official w-full">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Level</th>
                            <th>YaSHaRUN Status</th>
                            <th>Subscription Tier</th>
                            <th>Tags</th>
                            <th>Last Source</th>
                            <th>Updated</th>
                          </tr>
                        </thead>

                        <tbody>
                          {emailContacts.map((contact) => (
                            <tr key={contact.id || contact.email}>
                              <td className="font-medium">
                                {contact.full_name || "-"}
                              </td>
                              <td>{contact.email}</td>
                              <td>{contact.phone || "-"}</td>
                              <td>
                                <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs">
                                  {contact.email_level || "public"}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`px-2 py-1 rounded text-xs capitalize ${getStatusClass(
                                    contact.national_status
                                  )}`}
                                >
                                  {formatStatus(contact.national_status)}
                                </span>
                              </td>
                              <td>{contact.subscription_tier || "-"}</td>
                              <td className="min-w-[220px]">
                                {Array.isArray(contact.tags) &&
                                contact.tags.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {contact.tags.map((tag) => (
                                      <span
                                        key={tag}
                                        className="px-2 py-1 bg-kingdom-gold/10 text-kingdom-gold rounded text-xs"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td>{contact.last_source || "-"}</td>
                              <td className="text-sm text-slate-500">
                                {formatDate(contact.updated_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {applicationModalOpen && selectedApplication && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-5xl rounded-xl shadow-2xl my-8">
            <div className="flex items-center justify-between gap-4 border-b p-5">
              <div>
                <h2 className="font-heading text-2xl font-bold text-royal-blue">
                  YaSHaRUN Application
                </h2>
                <p className="text-sm text-slate-500">
                  {selectedApplication.full_name} • {selectedApplication.email}
                </p>
              </div>

              <button
                type="button"
                onClick={closeApplication}
                className="p-2 rounded hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg text-royal-blue">
                      Applicant Information
                    </h3>
                    <span
                      className={`px-3 py-1 rounded-full text-sm capitalize ${getStatusClass(
                        selectedApplication.status
                      )}`}
                    >
                      {formatStatus(selectedApplication.status)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Name</p>
                      <p className="font-medium">
                        {selectedApplication.full_name || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Email</p>
                      <p className="font-medium">
                        {selectedApplication.email || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Phone</p>
                      <p className="font-medium">
                        {selectedApplication.phone || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Country</p>
                      <p className="font-medium">
                        {selectedApplication.country || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">City</p>
                      <p className="font-medium">
                        {selectedApplication.city || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">
                        State / Province / Region
                      </p>
                      <p className="font-medium">
                        {selectedApplication.state_province_region || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Created</p>
                      <p className="font-medium">
                        {formatDateTime(selectedApplication.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Updated</p>
                      <p className="font-medium">
                        {formatDateTime(selectedApplication.updated_at)}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-slate-500 text-sm mb-2">Social Links</p>
                    {splitLinks(selectedApplication.social_links).length > 0 ? (
                      <div className="space-y-2">
                        {splitLinks(selectedApplication.social_links).map(
                          (link) => (
                            <a
                              key={link}
                              href={normalizeUrl(link)}
                              target="_blank"
                              rel="noreferrer"
                              className="text-royal-blue hover:underline flex items-center gap-2 text-sm break-all"
                            >
                              <ExternalLink className="w-4 h-4 shrink-0" />
                              {link}
                            </a>
                          )
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">None provided</p>
                    )}
                  </div>

                  <div>
                    <p className="text-slate-500 text-sm mb-2">
                      Preferred Ministries
                    </p>
                    {getApplicationMinistries(selectedApplication).length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {getApplicationMinistries(selectedApplication).map(
                          (ministry) => (
                            <span
                              key={ministry}
                              className="px-2 py-1 bg-royal-purple/10 text-royal-purple rounded text-xs"
                            >
                              {getMinistryLabel(ministry)}
                            </span>
                          )
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">None selected</p>
                    )}
                  </div>

                  <div>
                    <p className="text-slate-500 text-sm mb-2">
                      Reason for Joining
                    </p>
                    <p className="text-sm whitespace-pre-wrap bg-slate-50 p-3 rounded">
                      {selectedApplication.reason_for_joining || "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-slate-500 text-sm mb-2">
                      Skills / Offerings
                    </p>
                    <p className="text-sm whitespace-pre-wrap bg-slate-50 p-3 rounded">
                      {selectedApplication.skills_offerings || "-"}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      {selectedApplication.agree_to_constitution ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-scarlett" />
                      )}
                      <span className="text-sm">Constitution Agreement</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {selectedApplication.agree_to_covenant ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-scarlett" />
                      )}
                      <span className="text-sm">Internal Covenant</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5 space-y-4">
                  <h3 className="font-bold text-lg text-royal-blue">
                    Review / Decision
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Status
                    </label>
                    <select
                      value={reviewForm.status}
                      onChange={(event) =>
                        setReviewForm((current) => ({
                          ...current,
                          status: event.target.value,
                        }))
                      }
                      className="w-full border border-slate-300 rounded px-3 py-2"
                    >
                      <option value="pending">Pending</option>
                      <option value="needs_more_info">Needs More Info</option>
                      <option value="probationary">Probationary</option>
                      <option value="approved">Approved</option>
                      <option value="denied">Denied</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Review Notes
                    </label>
                    <textarea
                      value={reviewForm.review_notes}
                      onChange={(event) =>
                        setReviewForm((current) => ({
                          ...current,
                          review_notes: event.target.value,
                        }))
                      }
                      className="w-full border border-slate-300 rounded px-3 py-2 min-h-[90px]"
                      placeholder="Private admin notes..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Decision Reason
                    </label>
                    <textarea
                      value={reviewForm.decision_reason}
                      onChange={(event) =>
                        setReviewForm((current) => ({
                          ...current,
                          decision_reason: event.target.value,
                        }))
                      }
                      className="w-full border border-slate-300 rounded px-3 py-2 min-h-[90px]"
                      placeholder="Reason for approval, denial, or more info..."
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Probation Status
                      </label>
                      <input
                        value={reviewForm.probation_status}
                        onChange={(event) =>
                          setReviewForm((current) => ({
                            ...current,
                            probation_status: event.target.value,
                          }))
                        }
                        className="w-full border border-slate-300 rounded px-3 py-2"
                        placeholder="ex: 30-day review"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Probation Until
                      </label>
                      <input
                        type="date"
                        value={reviewForm.probation_until}
                        onChange={(event) =>
                          setReviewForm((current) => ({
                            ...current,
                            probation_until: event.target.value,
                          }))
                        }
                        className="w-full border border-slate-300 rounded px-3 py-2"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Probation Notes
                    </label>
                    <textarea
                      value={reviewForm.probation_notes}
                      onChange={(event) =>
                        setReviewForm((current) => ({
                          ...current,
                          probation_notes: event.target.value,
                        }))
                      }
                      className="w-full border border-slate-300 rounded px-3 py-2 min-h-[80px]"
                      placeholder="Probation conditions or watch notes..."
                    />
                  </div>

                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={reviewForm.send_email}
                      onChange={(event) =>
                        setReviewForm((current) => ({
                          ...current,
                          send_email: event.target.checked,
                        }))
                      }
                    />
                    Send applicant email on approve/deny
                  </label>

                  {selectedApplication.linked_username && (
                    <div className="bg-green-50 border border-green-200 text-green-800 rounded p-3 text-sm">
                      Linked Portal User:{" "}
                      <strong>{selectedApplication.linked_username}</strong>
                    </div>
                  )}

                  {selectedApplication.welcome_email_sent && (
                    <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded p-3 text-sm">
                      Welcome email sent:{" "}
                      {formatDateTime(selectedApplication.welcome_email_sent_at)}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleReviewSave}
                      disabled={actionLoading}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Review
                    </Button>

                    <Button
                      type="button"
                      onClick={handleApprove}
                      disabled={actionLoading}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>

                    <Button
                      type="button"
                      onClick={handleDeny}
                      disabled={actionLoading}
                      className="bg-scarlett hover:bg-scarlett/90 text-white"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Deny
                    </Button>
                  </div>

                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
                    <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                    <p>
                      Approving can create or update a portal user account.
                      Denying changes the application status but keeps the record
                      for review history.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
